import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import { throwFunctionsInvokeError } from '../../../lib/functionsInvoke';
import { notify } from '../../../utils/toast';
import { CURRENT_ORG_STORAGE_KEY } from '../../../contexts/OrganizationContext';
import {
    acceptInvite,
    buildInvitePath,
    clearInviteToken,
    getInviteDetails,
    getPendingInviteToken,
    persistInviteToken,
} from '../../../services/modules/organizationService';
import type { PlanId } from '../../../domain/entities/Subscription';
import { isPasswordAcceptable } from '../domain/passwordStrength';
import { getGaClientId } from '../../../lib/analytics/gaClientId';
import { markPendingOnboarding, clearPendingOnboarding } from '../../onboarding/pendingOnboarding';
import { resolvePartnerRefForSignup } from '../../../lib/partners/partnerRef';

/** Pré-seleção de plano via ?plan= (ex.: /registro?plan=pro). Inválido → basic. */
export function resolvePlanParam(search: string): PlanId {
    const value = new URLSearchParams(search).get('plan')?.toLowerCase();
    return value === 'basic' || value === 'pro' || value === 'enterprise' ? value : 'basic';
}

interface UseRegisterProps {
    onNavigateToLogin: () => void;
}

export const useRegister = ({ onNavigateToLogin }: UseRegisterProps) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('+55 ');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [plan, setPlan] = useState<PlanId>(() => resolvePlanParam(window.location.search));
    const [isRegistering, setIsRegistering] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [inviteDetails, setInviteDetails] = useState<{ email: string; organization_name: string } | null>(null);
    const [isLoadingInvite, setIsLoadingInvite] = useState(false);

    const inviteToken = useMemo(() => getPendingInviteToken(), []);

    useEffect(() => {
        const checkInvite = async () => {
            if (!inviteToken) return;

            setIsLoadingInvite(true);
            try {
                const details = await getInviteDetails(inviteToken);
                if (details) {
                    setInviteDetails(details);
                    setEmail(details.email);
                }
            } catch (err) {
                console.error('Error checking invite:', err);
            } finally {
                setIsLoadingInvite(false);
            }
        };

        checkInvite();
    }, [inviteToken]);

    // Mantém a orientação visível no formulário mesmo depois que o toast desaparece.
    const showWeakPasswordError = (message: string) => {
        setError(message);
        notify.error('Escolha uma senha mais segura', {
            description: message,
            duration: 8000,
            id: 'signup-weak-password',
        });
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        const phoneDigits = phone.replace(/\D/g, '');
        
        if (!name.trim() || !email.trim() || !phone.trim() || !password || !confirmPassword) {
            setError('Por favor, preencha todos os campos obrigatórios.');
            return;
        }

        if (phoneDigits.length < 12) {
            setError('Informe um telefone WhatsApp válido com DDD.');
            return;
        }

        if (password !== confirmPassword) {
            setError('As senhas não coincidem.');
            return;
        }

        // Regra de Segurança #7: Validação de complexidade/tamanho antes do envio
        if (!isPasswordAcceptable(password)) {
            showWeakPasswordError(
                'Use uma senha mais forte: pelo menos 8 caracteres, misturando maiúsculas, minúsculas, números e símbolos.',
            );
            return;
        }

        setError(null);
        setSuccessMessage(null);
        setIsRegistering(true);

        if (inviteToken) {
            persistInviteToken(inviteToken);
            // Garantir que NUNCA sete a flag de novo registro quando há convite
            localStorage.removeItem('adlitics_new_registration');
        } else {
            // Setar flags antes do auth-signup para o gate ir direto ao onboarding (sem flash no dashboard)
            localStorage.setItem('adlitics_new_registration', 'true');
            markPendingOnboarding();
        }

        try {
            const ga_client_id = getGaClientId();
            const partner_code = inviteToken ? null : resolvePartnerRefForSignup(window.location.search);

            const { data, error } = await supabase.functions.invoke('auth-signup', {
                body: {
                    email,
                    password,
                    full_name: name,
                    phone,
                    invite_token: inviteToken || null,
                    ga_client_id,
                    ...(inviteToken ? {} : { plan }),
                    ...(partner_code ? { partner_code } : {}),
                },
            });

            if (data?.error) {
                const signupError = new Error(data.error) as Error & { code?: string };
                if (data.code === 'weak_password') signupError.code = 'weak_password';
                throw signupError;
            }

            if (error) {
                await throwFunctionsInvokeError(data, error, 'Falha ao cadastrar. Tente novamente.');
            }

            const { error: signInError } = await supabase.auth.signInWithPassword({
                email,
                password,
            });
            if (signInError) {
                throw signInError;
            }

            // CTA "Criar conta e aceitar": ingressa na org do convite (role do invite), sem criar org nova.
            if (inviteToken) {
                const result = await acceptInvite(inviteToken);
                if (!result?.success) {
                    window.history.replaceState(null, '', buildInvitePath(inviteToken));
                    throw new Error(result?.error || 'Conta criada, mas não foi possível aceitar o convite.');
                }
                clearInviteToken();
                const orgId = result.organization_id as string | undefined;
                if (orgId) localStorage.setItem(CURRENT_ORG_STORAGE_KEY, orgId);
            }
        } catch (err: any) {
            console.error('Registration error:', err);

            // Mantém token do convite para retry; só limpa flags de org nova.
            localStorage.removeItem('adlitics_new_registration');
            clearPendingOnboarding();

            // Traduzir erros comuns do Supabase para português
            let errorMessage = err.message || 'Falha ao cadastrar. Tente novamente.';
            const lower = errorMessage.toLowerCase();

            if (lower.includes('failed to fetch') ||
                lower.includes('networkerror') ||
                lower.includes('err_connection')) {
                errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
            } else if (lower.includes('already registered') || lower.includes('já está em uso')) {
                errorMessage = 'Este email já está em uso.';
            } else if (err.code === 'weak_password') {
                errorMessage =
                    'Use uma senha mais forte: pelo menos 8 caracteres, misturando maiúsculas, minúsculas, números e símbolos.';
            } else if (lower.includes('password') || lower.includes('invalid login')) {
                errorMessage = 'Conta criada, mas não foi possível entrar. Faça login manualmente.';
            } else if (
                err.status === 429 ||
                lower.includes('rate limit') ||
                lower.includes('email rate limit')
            ) {
                errorMessage =
                    'Limite de envio de emails atingido. Aguarde cerca de 1 hora e tente novamente.';
            }

            if (err.code === 'weak_password' ||
                lower.startsWith('esta senha foi recusada por segurança') ||
                lower.startsWith('use uma senha mais forte')) {
                showWeakPasswordError(errorMessage);
            } else {
                setError(errorMessage);
            }
        } finally {
            setIsRegistering(false);
        }
    };

    return {
        name, setName,
        email, setEmail,
        phone, setPhone,
        plan, setPlan,
        password, setPassword,
        confirmPassword, setConfirmPassword,
        isRegistering,
        error,
        successMessage,
        inviteDetails,
        isLoadingInvite,
        inviteToken,
        handleRegister
    };
};
